import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { patientHistoryService } from '../services/patientHistoryService'
import { FamilyHistoryFormData, FamilyHistory } from '../types/patientHistory'
import './FamilyHistoryForm.css'

interface FamilyHistoryFormProps {
  patientId: string
  initialData?: FamilyHistory
  onSuccess: () => void
  onCancel: () => void
}

export function FamilyHistoryForm({ patientId, initialData, onSuccess, onCancel }: FamilyHistoryFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FamilyHistoryFormData>({
    defaultValues: initialData
      ? {
          relationship: initialData.relationship,
          condition: initialData.condition,
          ageAtDiagnosis: initialData.ageAtDiagnosis,
          ageAtDeath: initialData.ageAtDeath,
          causeOfDeath: initialData.causeOfDeath || '',
          notes: initialData.notes || '',
          isPertinent: initialData.isPertinent,
        }
      : {
          isPertinent: false,
        },
  })

  const onSubmit = async (data: FamilyHistoryFormData) => {
    setIsSubmitting(true)
    try {
      if (initialData) {
        await patientHistoryService.updateFamilyHistory(patientId, initialData.id, data)
      } else {
        await patientHistoryService.addFamilyHistory(patientId, data)
      }
      onSuccess()
    } catch (error) {
      console.error('Error saving family history:', error)
      alert('Failed to save family history')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="family-history-form">
      <div className="form-group">
        <label htmlFor="relationship">
          Relationship <span className="required">*</span>
        </label>
        <input
          id="relationship"
          type="text"
          {...register('relationship', { required: 'Relationship is required' })}
          className={errors.relationship ? 'error' : ''}
          placeholder="e.g., Mother, Father, Paternal Grandfather, Sister"
        />
        {errors.relationship && (
          <span className="error-message">{errors.relationship.message}</span>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="condition">
          Condition <span className="required">*</span>
        </label>
        <input
          id="condition"
          type="text"
          {...register('condition', { required: 'Condition is required' })}
          className={errors.condition ? 'error' : ''}
          placeholder="e.g., Type 2 Diabetes, Heart Disease, Cancer"
        />
        {errors.condition && (
          <span className="error-message">{errors.condition.message}</span>
        )}
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="ageAtDiagnosis">Age at Diagnosis</label>
          <input
            id="ageAtDiagnosis"
            type="number"
            min="0"
            max="120"
            {...register('ageAtDiagnosis', {
              valueAsNumber: true,
              min: { value: 0, message: 'Age must be positive' },
              max: { value: 120, message: 'Age must be reasonable' },
            })}
            placeholder="Years"
          />
          {errors.ageAtDiagnosis && (
            <span className="error-message">{errors.ageAtDiagnosis.message}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="ageAtDeath">Age at Death</label>
          <input
            id="ageAtDeath"
            type="number"
            min="0"
            max="120"
            {...register('ageAtDeath', {
              valueAsNumber: true,
            })}
            placeholder="Years"
          />
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="causeOfDeath">Cause of Death</label>
        <input
          id="causeOfDeath"
          type="text"
          {...register('causeOfDeath')}
          placeholder="e.g., Myocardial Infarction, Cancer"
        />
      </div>

      <div className="form-group">
        <label htmlFor="notes">Notes</label>
        <textarea
          id="notes"
          {...register('notes')}
          rows={3}
          placeholder="Additional notes about this family history..."
        />
      </div>

      <div className="form-group checkbox-group">
        <input
          id="isPertinent"
          type="checkbox"
          {...register('isPertinent')}
        />
        <label htmlFor="isPertinent">
          Mark as pertinent (particularly relevant to patient care)
        </label>
      </div>

      <div className="form-actions">
        <button
          type="button"
          onClick={onCancel}
          className="btn-secondary"
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn-primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving...' : initialData ? 'Update Entry' : 'Add Entry'}
        </button>
      </div>
    </form>
  )
}
